import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Megaphone, CheckCircle2, Image as ImageIcon, X as XIcon } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import FeedCard from '../components/FeedCard';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { placeholderFor } from '../utils/feedHelpers';
import { toast } from 'sonner';

/**
 * Formulário de publicação no Feed — 3 passos:
 *   1) Categoria do post (filtrada pela categoria do negócio)
 *   2) Texto + foto + (se evento-no-espaco) data_evento
 *   3) Preview + Publicar
 *
 * Rota: /empreendedor/feed/novo?negocio_id=<id>
 */
export default function NovoPostFeed() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [search, setSearch] = useSearchParams();
  const negocioId = search.get('negocio_id') || '';

  // Step 0 = escolher negócio; Step 1..3 = fluxo original.
  // Se já vier `?negocio_id=` na URL, pula para 1.
  const [step, setStep] = useState(negocioId ? 1 : 0);
  const [loading, setLoading] = useState(true);
  const [meusNegocios, setMeusNegocios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [negocioCat, setNegocioCat] = useState('');
  const [categoriaPost, setCategoriaPost] = useState(null); // {key, label, expiracao}
  const [texto, setTexto] = useState('');
  const [fotoUrl, setFotoUrl] = useState('');
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [dataEvento, setDataEvento] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [limiteAtingido, setLimiteAtingido] = useState(false);
  // Limite diário exposto pelo backend (/v2/feed/categorias). Default 1 post/dia.
  const [limiteDiario, setLimiteDiario] = useState(1);

  // Handler central de voltar — respeita o passo atual em vez de sair da página.
  const voltarPasso = () => {
    if (step === 3) return setStep(2);
    if (step === 2) return setStep(1);
    if (step === 1) {
      // Se veio direto (?negocio_id na URL), o botão volta para a origem.
      // Se veio do picker (step 0), volta para o picker.
      if (meusNegocios.length > 1) {
        setSearch({});
        setStep(0);
        setCategoriaPost(null);
        return;
      }
      return navigate(-1);
    }
    // step 0
    navigate(-1);
  };

  // Faz upload da foto do post no R2 via /upload/imagem (area=feed).
  // Retorna a URL "medium" que será persistida no post do feed.
  const handleFotoFile = async (file) => {
    if (!file) return;
    if (!negocioId) {
      toast.error('Selecione um negócio antes de anexar foto.');
      return;
    }
    // Prioriza o municipio_id do PRÓPRIO negócio — em contas com múltiplos
    // negócios em cidades diferentes, o user.municipio_id fica desatualizado.
    // Fallbacks: user.municipio_id → chosen municipio (URL/context).
    const municipioIdEff =
      negocioAtual?.municipio_id
      || user?.municipio_id
      || '';
    if (!municipioIdEff) {
      toast.error('Não foi possível identificar o município do negócio. Recarregue a página.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('A imagem excede 8MB. Comprima antes de enviar.');
      return;
    }
    if (file.type && !file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem.');
      return;
    }
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'foto.jpg');
      fd.append('municipio_id', municipioIdEff);
      fd.append('entidade_id', negocioId);
      fd.append('area', 'feed');
      // A instância `api` tem default Content-Type: application/json. Sem
      // sobrescrever para multipart, axios stringify o FormData e o FastAPI
      // devolve 422 com todos os Form(...) faltando. Axios injeta o boundary
      // correto automaticamente quando o body é FormData mesmo com essa flag.
      const { data } = await api.post('/upload/imagem', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data?.urls?.medium || data?.urls?.large || data?.urls?.thumb;
      if (!url) throw new Error('resposta sem URL');
      setFotoUrl(url);
      toast.success('Foto anexada!');
    } catch (err) {
      // Extrai a mensagem mais específica possível — detail pode ser string OU
      // objeto { erro, mensagem } (ex.: quando o limite de fotos por dia é
      // atingido). Sem isso, o usuário via genericamente "Erro ao enviar foto"
      // e não tinha como saber o motivo (403 permissão, 413 muito grande, etc.).
      const resp = err?.response;
      let msg = 'Erro ao enviar foto.';
      const d = resp?.data?.detail;
      if (typeof d === 'string') msg = d;
      else if (d && typeof d === 'object') msg = d.mensagem || d.erro || JSON.stringify(d);
      else if (resp?.status === 403) msg = 'Sem permissão para anexar foto neste negócio.';
      else if (resp?.status === 413) msg = 'Imagem grande demais. Comprima antes de enviar.';
      else if (resp?.status === 415) msg = 'Formato de imagem não suportado.';
      else if (resp?.status >= 500) msg = 'Servidor indisponível — tente novamente em instantes.';
      else if (!resp) msg = 'Falha de rede — verifique sua conexão.';
      // eslint-disable-next-line no-console
      console.error('[NovoPostFeed] upload foto falhou:', { status: resp?.status, detail: d, err });
      toast.error(msg);
    } finally {
      setUploadingFoto(false);
    }
  };

  // load categorias disponíveis para o negócio + meus posts (para checar limite)
  // + lista de negócios do usuário (para o step 0 - picker E para pegar logo/nome).
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setLoading(true);

    // Sempre carrega /my-businesses para: (a) picker se sem negocio_id, (b)
    // achar o logo/nome do negócio corrente pra usar no preview do post.
    const bizPromise = api.get('/my-businesses')
      .then((r) => (r.data || []).filter((b) => !b.is_draft))
      .catch(() => []);

    if (!negocioId) {
      bizPromise
        .then((list) => {
          setMeusNegocios(list);
          if (list.length === 1) {
            // Auto-seleciona e injeta na URL para manter shareable link.
            setSearch({ negocio_id: list[0].id }, { replace: true });
          }
        })
        .finally(() => setLoading(false));
      return;
    }

    // Com negocio_id → carrega categorias + limite diário + lista de negócios.
    Promise.all([
      api.get('/v2/feed/empreendedor/categorias-disponiveis', { params: { negocio_id: negocioId } }),
      api.get('/v2/empreendedor/feed/posts', { params: { negocio_id: negocioId } }).catch(() => ({ data: [] })),
      bizPromise,
      // Busca o teto de posts/dia do backend (default 1). Assim se o admin
      // alterar via env FEED_POSTS_PER_DAY_LIMIT, a UI se ajusta sozinha.
      api.get('/v2/feed/categorias').then(r => r.data?.limite_diario || 1).catch(() => 1),
    ])
      .then(([rc, rp, bizList, limite]) => {
        setCategorias(rc.data?.categorias || []);
        setNegocioCat(rc.data?.negocio_categoria || '');
        setMeusNegocios(bizList);
        setLimiteDiario(limite);
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
        const postsHoje = (rp.data || []).filter((p) => new Date(p.criado_em).getTime() >= hoje.getTime());
        setLimiteAtingido(postsHoje.length >= limite);
      })
      .catch(() => setCategorias([]))
      .finally(() => setLoading(false));
  }, [user, negocioId, navigate, setSearch]);

  // Negócio corrente (usado no preview e header do post).
  const negocioAtual = useMemo(
    () => meusNegocios.find((b) => b.id === negocioId) || null,
    [meusNegocios, negocioId]
  );

  const previewPost = useMemo(() => {
    if (!categoriaPost) return null;
    return {
      id: 'preview',
      negocio_nome: negocioAtual?.nome || 'Seu negócio',
      negocio_categoria: negocioCat,
      // Preview usa o LOGO do negócio (thumb/medium) — antes usava user.picture
      // (avatar do usuário), o que era enganoso quando o dono tem vários negócios
      // ou perfil sem foto. FeedCard já resolve URL relativa (/api/uploads/...).
      negocio_foto: negocioAtual?.imagens?.logo || negocioAtual?.avatar_url || null,
      negocio_nivel_irn: negocioAtual?.nivel_irn || 0,
      categoria_post: categoriaPost.key,
      texto: texto || placeholderFor(categoriaPost.key),
      foto_url: fotoUrl,
      expira_em: calcExpiraPreview(categoriaPost.expiracao, dataEvento),
      criado_em: new Date().toISOString(),
    };
  }, [categoriaPost, texto, fotoUrl, dataEvento, negocioAtual, negocioCat]);

  const categoriasMap = useMemo(() => {
    const m = {}; categorias.forEach((c) => { m[c.key] = c.label; }); return m;
  }, [categorias]);

  function avancarParaTexto(cat) {
    setCategoriaPost(cat);
    setStep(2);
  }

  async function publicar() {
    if (!categoriaPost || !texto.trim()) {
      toast.error('Selecione a categoria e escreva o texto.');
      return;
    }
    if (texto.length > 280) {
      toast.error('Texto excede 280 caracteres.');
      return;
    }
    if (categoriaPost.expiracao === 'data_evento' && !dataEvento) {
      toast.error('Informe a data do evento.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        categoria_post: categoriaPost.key,
        texto: texto.trim(),
      };
      if (negocioId) body.negocio_id = negocioId;
      if (fotoUrl) body.foto_url = fotoUrl;
      if (dataEvento) body.data_evento = new Date(dataEvento).toISOString();
      const r = await api.post('/v2/feed/posts', body);
      const msgExp = formataMsgExpiracao(r.data?.expira_em);
      toast.success(`Publicado! ${msgExp}`);
      navigate(negocioId ? `/empreendedor/feed/posts?negocio_id=${encodeURIComponent(negocioId)}` : '/empreendedor/feed/posts');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Erro ao publicar.';
      if (status === 429) toast.error(detail || 'Limite diário atingido.');
      else if (status === 409) toast.error(detail);
      else toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="p-12 text-center text-gray-500">Carregando…</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="feed-novo-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={voltarPasso}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-purple-700 mb-3"
          data-testid="feed-back-top"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-1">
          <Megaphone className="w-6 h-6 text-purple-600" /> Publicar no Feed
        </h1>
        {negocioCat && step >= 1 && <p className="text-sm text-gray-500 mb-5">Negócio · {negocioCat}</p>}

        {/* Step 0 — escolha do negócio (apenas quando não há ?negocio_id na URL). */}
        {step === 0 && (
          <div data-testid="feed-step-0" className="space-y-4">
            <p className="text-sm text-gray-600">Escolha para qual negócio você quer publicar o post:</p>
            {meusNegocios.length === 0 ? (
              <div className="p-6 rounded-xl border-2 border-dashed border-gray-300 text-center text-sm text-gray-500">
                Você ainda não tem negócios cadastrados. Cadastre um antes de publicar no feed.
                <div className="mt-3">
                  <Button asChild variant="outline">
                    <Link to="/cadastrar">Cadastrar negócio</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {meusNegocios.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => {
                      setSearch({ negocio_id: b.id }, { replace: true });
                      setStep(1);
                    }}
                    className="text-left p-3 rounded-xl bg-white border border-gray-200 hover:border-purple-400 hover:shadow-md transition"
                    data-testid={`feed-picker-biz-${b.id}`}
                  >
                    <div className="text-sm font-medium truncate">{b.nome}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {b.categoria_nome || b.categoria || '—'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step >= 1 && (limiteAtingido ? (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 text-center" data-testid="feed-novo-limite">
            <p className="text-amber-900 font-semibold">
              Você atingiu o limite de {limiteDiario} {limiteDiario === 1 ? 'post' : 'posts'} hoje.
            </p>
            <p className="text-sm text-amber-700 mt-1">Volte amanhã para publicar mais novidades!</p>
            <Button asChild className="mt-4" variant="outline">
              <Link to={negocioId ? `/empreendedor/feed/posts?negocio_id=${negocioId}` : '/empreendedor/feed/posts'}>Ver meus posts</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Stepper */}
            <ol className="flex items-center gap-2 mb-6 text-xs">
              {['Categoria', 'Texto', 'Preview'].map((s, i) => (
                <li key={s} className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold ${
                    step >= i + 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>{i + 1}</span>
                  <span className={step === i + 1 ? 'font-semibold text-purple-700' : 'text-gray-500'}>{s}</span>
                  {i < 2 && <ArrowRight className="w-3 h-3 text-gray-400" />}
                </li>
              ))}
            </ol>

            {/* Step 1: categoria */}
            {step === 1 && (
              <div data-testid="feed-step-1">
                <p className="text-sm text-gray-600 mb-3">Escolha que tipo de novidade você vai postar:</p>
                {categorias.length === 0 ? (
                  <p className="text-sm text-gray-500">Nenhuma categoria disponível para o seu negócio.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {categorias.map((c) => (
                      <button
                        key={c.key}
                        onClick={() => avancarParaTexto(c)}
                        data-testid={`feed-cat-${c.key}`}
                        className="text-left p-3 rounded-xl bg-white border border-gray-200 hover:border-purple-400 hover:shadow-md transition"
                      >
                        <div className="text-sm font-medium">{c.label}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {formataExpiracaoLabel(c.expiracao)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: texto */}
            {step === 2 && categoriaPost && (
              <div className="space-y-4" data-testid="feed-step-2">
                <div className="p-3 rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase font-bold text-purple-700">Categoria</div>
                    <div className="font-medium text-sm">{categoriaPost.label}</div>
                  </div>
                  <button onClick={() => setStep(1)} className="text-xs text-purple-700 hover:underline">Trocar</button>
                </div>

                <div>
                  <Label htmlFor="feed-texto" className="mb-1 block">Texto do post</Label>
                  <Textarea
                    id="feed-texto"
                    rows={4}
                    maxLength={280}
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder={placeholderFor(categoriaPost.key)}
                    data-testid="feed-texto-input"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1" data-testid="feed-texto-counter">
                    {280 - texto.length} caracteres restantes
                  </div>
                </div>

                <div>
                  <Label className="mb-1 block flex items-center gap-1">
                    <ImageIcon className="w-3.5 h-3.5" /> Foto (opcional)
                  </Label>
                  {fotoUrl ? (
                    <div
                      className="relative inline-block rounded-lg overflow-hidden border border-gray-200"
                      data-testid="feed-foto-preview"
                    >
                      <img
                        src={fotoUrl}
                        alt="Preview"
                        className="max-h-40 w-auto object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFotoUrl('')}
                        className="absolute top-1 right-1 bg-white/95 hover:bg-white rounded-full p-1 shadow-sm"
                        title="Remover foto"
                        data-testid="feed-foto-remove"
                      >
                        <XIcon className="w-3.5 h-3.5 text-red-600" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="feed-foto-file"
                      className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg px-4 py-6 text-sm cursor-pointer transition-colors ${
                        uploadingFoto
                          ? 'border-purple-300 bg-purple-50 text-purple-700'
                          : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50 text-gray-600'
                      }`}
                      data-testid="feed-foto-dropzone"
                    >
                      <ImageIcon className="w-5 h-5" />
                      {uploadingFoto ? 'Enviando foto…' : 'Clique para escolher uma foto do seu dispositivo'}
                      <input
                        id="feed-foto-file"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingFoto}
                        onChange={(e) => handleFotoFile(e.target.files?.[0])}
                        data-testid="feed-foto-input"
                      />
                    </label>
                  )}
                </div>

                {categoriaPost.expiracao === 'data_evento' && (
                  <div>
                    <Label htmlFor="feed-data-evento" className="mb-1 block">Data do evento *</Label>
                    <Input
                      id="feed-data-evento"
                      type="datetime-local"
                      value={dataEvento}
                      onChange={(e) => setDataEvento(e.target.value)}
                      data-testid="feed-data-evento-input"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">O post expira automaticamente quando o evento acontecer.</p>
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(1)}>← Voltar</Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!texto.trim() || (categoriaPost.expiracao === 'data_evento' && !dataEvento)}
                    data-testid="feed-step-2-next"
                  >
                    Preview →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: preview */}
            {step === 3 && previewPost && (
              <div className="space-y-4" data-testid="feed-step-3">
                <p className="text-sm text-gray-600">Veja como seu post vai aparecer no feed:</p>
                <FeedCard post={previewPost} variant="full" categoriasMap={categoriasMap} />
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-900 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Este post expira <strong>{formataMsgExpiracao(previewPost.expira_em)}</strong>.
                  </span>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(2)}>← Editar</Button>
                  <Button onClick={publicar} disabled={submitting} data-testid="feed-publicar-btn">
                    {submitting ? 'Publicando…' : 'Publicar agora'}
                  </Button>
                </div>
              </div>
            )}
          </>
        ))}
      </div>
    </div>
  );
}

// ============== helpers locais ==============

function formataExpiracaoLabel(exp) {
  if (!exp) return '';
  if (exp === 'meia_noite') return 'Some à meia-noite';
  if (exp === 'fim_expediente') return 'Some ao fim do expediente';
  if (exp === 'data_evento') return 'Some quando o evento acabar';
  if (exp.startsWith('horas_')) {
    const n = exp.split('_')[1];
    return `Some em ${n} ${n === '1' ? 'hora' : 'horas'}`;
  }
  if (exp.startsWith('dias_')) {
    const n = exp.split('_')[1];
    return `Some em ${n} ${n === '1' ? 'dia' : 'dias'}`;
  }
  return '';
}

function calcExpiraPreview(exp, dataEvento) {
  const now = new Date();
  if (exp === 'meia_noite') {
    const d = new Date(now);
    d.setHours(23, 59, 59, 0);
    return d.toISOString();
  }
  if (exp === 'fim_expediente') {
    const d = new Date(now);
    d.setHours(20, 0, 0, 0); // estimativa
    return d.toISOString();
  }
  if (exp === 'data_evento' && dataEvento) {
    return new Date(dataEvento).toISOString();
  }
  if (exp?.startsWith('horas_')) {
    const n = parseInt(exp.split('_')[1], 10);
    return new Date(now.getTime() + n * 3600 * 1000).toISOString();
  }
  if (exp?.startsWith('dias_')) {
    const n = parseInt(exp.split('_')[1], 10);
    return new Date(now.getTime() + n * 24 * 3600 * 1000).toISOString();
  }
  return new Date(now.getTime() + 24 * 3600 * 1000).toISOString();
}

function formataMsgExpiracao(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return `hoje às ${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`;
  const ms = d.getTime() - now.getTime();
  const dias = Math.ceil(ms / (1000 * 3600 * 24));
  if (dias <= 0) return 'em breve';
  return `em ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}
